import {
  CollegeProgram,
  PROGRAM_CREDENTIAL_LEVELS,
  ProgramCredentialLevel,
} from '@backrs_ui/common-ui';

import { categoryById, categoryOrder, ProgramCategory } from './programCategories';

export type ProgramEarningsSortKey = 'program' | 'awards' | 'earnings';
export type ProgramEarningsSortDirection = 'asc' | 'desc';

export type ProgramEarningsSort = Readonly<{
  key: ProgramEarningsSortKey;
  direction: ProgramEarningsSortDirection;
}>;

export const hasCurrentAwards = (program: CollegeProgram): boolean =>
  (program.awards.latest ?? 0) > 0;

export const hasReportedEarnings = (program: CollegeProgram): boolean =>
  program.earnings.yr4.median != null;

export const programsAtLevel = (
  programs: readonly CollegeProgram[],
  level: ProgramCredentialLevel,
): readonly CollegeProgram[] => programs.filter((program) => program.credlev === level);

export const isLevelVisible = (programs: readonly CollegeProgram[]): boolean =>
  programs.some(hasCurrentAwards);

export const visibleLevels = (
  programs: readonly CollegeProgram[],
): readonly ProgramCredentialLevel[] =>
  [PROGRAM_CREDENTIAL_LEVELS.BACHELORS, PROGRAM_CREDENTIAL_LEVELS.MASTERS].filter((level) =>
    isLevelVisible(programsAtLevel(programs, level)),
  );

export type CategoryShare = Readonly<{
  category: ProgramCategory;
  awards: number;
  share: number;
}>;

export type ProgramShare = Readonly<{
  program: CollegeProgram;
  share: number;
}>;

export type DegreeMix = Readonly<{
  totalAwards: number;
  programCount: number;
  categories: readonly CategoryShare[];
  largestCategory: CategoryShare | null;
  largestProgram: ProgramShare | null;
}>;

export const computeDegreeMix = (levelPrograms: readonly CollegeProgram[]): DegreeMix => {
  const current = levelPrograms.filter(hasCurrentAwards);
  const totalAwards = current.reduce((sum, program) => sum + (program.awards.latest ?? 0), 0);

  const awardsByCategory = new Map<string, CategoryShare>();

  for (const program of current) {
    const category = categoryById(program.category);
    const existing = awardsByCategory.get(category.id);
    const awards = (existing?.awards ?? 0) + (program.awards.latest ?? 0);

    awardsByCategory.set(category.id, { category, awards, share: 0 });
  }

  const categories = Array.from(awardsByCategory.values())
    .map((entry) => ({ ...entry, share: totalAwards ? entry.awards / totalAwards : 0 }))
    .sort((a, b) => categoryOrder(a.category) - categoryOrder(b.category));

  const largestCategory = categories.reduce<CategoryShare | null>(
    (best, entry) => (best == null || entry.awards > best.awards ? entry : best),
    null,
  );

  const largest = current.reduce<CollegeProgram | null>(
    (best, program) =>
      best == null || (program.awards.latest ?? 0) > (best.awards.latest ?? 0) ? program : best,
    null,
  );

  return {
    totalAwards,
    programCount: current.length,
    categories,
    largestCategory,
    largestProgram:
      largest && totalAwards
        ? { program: largest, share: (largest.awards.latest ?? 0) / totalAwards }
        : null,
  };
};

export type EarningsRange = Readonly<{ low: number; high: number }>;

export type EarningsStats = Readonly<{
  /** Programs with current-year awards: the same population as the degree mix. */
  currentProgramCount: number;
  /** Programs with current-year awards that also report a 4-year median. */
  reportingProgramCount: number;
  median: number;
  iqr: EarningsRange;
  range: EarningsRange;
  lowestProgram: CollegeProgram;
  highestProgram: CollegeProgram;
}>;

/** Linear-interpolated percentile (0–1) of an ascending list. */
const percentileOf = (sorted: readonly number[], p: number): number => {
  const position = (sorted.length - 1) * p;
  const lower = Math.floor(position);
  const upper = Math.ceil(position);

  return sorted[lower] + (sorted[upper] - sorted[lower]) * (position - lower);
};

/** Unweighted statistics over program-level 4-year medians. */
export const computeEarningsStats = (
  levelPrograms: readonly CollegeProgram[],
): EarningsStats | null => {
  const current = levelPrograms.filter(hasCurrentAwards);
  const reporting = current.filter(hasReportedEarnings);

  if (reporting.length === 0) return null;

  const byMedian = [...reporting].sort(
    (a, b) => (a.earnings.yr4.median ?? 0) - (b.earnings.yr4.median ?? 0),
  );
  const medians = byMedian.map((program) => program.earnings.yr4.median ?? 0);

  return {
    currentProgramCount: current.length,
    reportingProgramCount: reporting.length,
    median: percentileOf(medians, 0.5),
    iqr: { low: percentileOf(medians, 0.25), high: percentileOf(medians, 0.75) },
    range: { low: medians[0], high: medians[medians.length - 1] },
    lowestProgram: byMedian[0],
    highestProgram: byMedian[byMedian.length - 1],
  };
};

/** Every program at the level with a reported 4-year median, including those with no current-year awards. */
export const sortEarningsRows = (
  levelPrograms: readonly CollegeProgram[],
  key: ProgramEarningsSortKey,
  direction: ProgramEarningsSortDirection,
): readonly CollegeProgram[] => {
  const sign = direction === 'asc' ? 1 : -1;
  const rows = levelPrograms.filter(hasReportedEarnings);

  return [...rows].sort((a, b) => {
    if (key === 'program') return sign * a.cip_desc.localeCompare(b.cip_desc);
    if (key === 'earnings') {
      return sign * ((a.earnings.yr4.median ?? 0) - (b.earnings.yr4.median ?? 0));
    }

    const aHas = hasCurrentAwards(a);
    const bHas = hasCurrentAwards(b);

    if (aHas !== bHas) return aHas ? -1 : 1;

    return sign * ((a.awards.latest ?? 0) - (b.awards.latest ?? 0));
  });
};
